import {
	Autocomplete,
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	IconButton,
	IconButtonProps,
	styled,
	TextField,
} from '@mui/material'
import * as React from 'react'
import { Dispatch, SetStateAction } from 'react'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DatePicker } from '@mui/x-date-pickers'
import { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import { useProjectFeaturesContext } from '../project/contexts/ProjectFeaturesContext'
import { ScaffoldFilters } from '../../types/Scaffold/Scaffold'

dayjs.extend(timezone)

interface ExpandMoreProps extends IconButtonProps {
	expand: boolean
}

const ExpandMore = styled((props: ExpandMoreProps) => {
	const { expand, ...other } = props
	return <IconButton {...other} />
})(({ theme, expand }: { theme: any; expand: boolean }) => ({
	marginLeft: 'auto',
	transition: theme.transitions.create('transform', {
		duration: theme.transitions.duration.shortest,
	}),
	transform: expand ? 'rotate(180deg)' : 'rotate(0deg)',
}))

type FilterHeaderProps = {
	filters: ScaffoldFilters
	setFilters: Dispatch<SetStateAction<ScaffoldFilters>>
}

function FilterHeader({ filters, setFilters }: FilterHeaderProps) {
	const [expanded, setExpanded] = React.useState(true)
	const { projectTimeZone } = useProjectFeaturesContext()

	const handleExpandClick = () => {
		setExpanded(!expanded)
	}

	const handleFilterChange = (field: string, value: any) => {
		setFilters((prev: ScaffoldFilters) => ({
			...prev,
			[field]: value,
			page: 0,
		}))
	}

	const handleDateChange = (field: string, date: Dayjs | null) => {
		if (date) {
			const utcDate = date.tz(projectTimeZone || 'UTC').utc()
			handleFilterChange(field, utcDate)
		} else {
			handleFilterChange(field, null)
		}
	}

	function handleClearFilters(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
		setFilters({
			scaffoldRequestId: null,
			status: null,
			tagNumber: null,
			page: 0,
			requestedOn: null,
			action: null,
			priority: null,
			requiredBefore: null,
		})
	}

	const actionOptions = ['Erect', 'Modify', 'Dismantle']
	const priorityOptions = ['Low', 'Medium', 'High', 'Urgent']

	return (
		<Card sx={{ maxHeight: '20%', mt: 1}} elevation={2}>
			<CardHeader
				sx={{ py: 0, cursor: 'pointer' }}
				subheader={
					<>
						Filters
						<ExpandMore
							expand={expanded}
							onClick={handleExpandClick}
							aria-expanded={expanded}
							aria-label='show more'
						>
							<ExpandMoreIcon />
						</ExpandMore>
					</>
				}
				onClick={handleExpandClick}
			/>
			<CardContent sx={{ py: 0, '&:last-child': { pb: 2 } }}>
				<Collapse in={expanded} timeout={200} sx={{ pb: 1 }}>
					<Box
						sx={{
							display: 'flex',
							flexWrap: 'wrap',
							gap: 2,
							width: '90%',
							p: 0,
						}}
					>
						<Box sx={{ flex: '0 1 5%' }}>
							<TextField
								label='ID'
								variant='filled'
								fullWidth
								size='small'
								type='number'
								value={filters.scaffoldRequestId ?? ''}
								onChange={(e) => handleFilterChange('scaffoldRequestId', e.target.value)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<TextField
								label='Tag Number'
								variant='filled'
								fullWidth
								size='small'
								type='number'
								value={filters.tagNumber ?? ''}
								onChange={(e) => handleFilterChange('tagNumber', e.target.value)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<Autocomplete
								disablePortal
								key={filters.status ?? 'empty'}
								options={['Pending', 'In Progress', 'Completed', 'Cancelled']}
								value={filters.status}
								onChange={(_, newValue) => handleFilterChange('status', newValue)}
								renderInput={(params) => (
									<TextField {...params} label='Status' variant='filled' size='small' />
								)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<Autocomplete
								disablePortal
								key={filters.action ?? 'empty'}
								options={actionOptions}
								value={filters.action}
								onChange={(_, newValue) => handleFilterChange('action', newValue)}
								renderInput={(params) => (
									<TextField {...params} label='Action' variant='filled' size='small' />
								)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<Autocomplete
								disablePortal
								key={filters.priority ?? 'empty'}
								options={priorityOptions}
								value={filters.priority}
								onChange={(_, newValue) => handleFilterChange('priority', newValue)}
								renderInput={(params) => (
									<TextField {...params} label='Priority' variant='filled' size='small' />
								)}
							/>
						</Box>
						<Box sx={{ flex: '0 1 12%' }}>
							<DatePicker
								label='Requested On'
								value={filters.requestedOn}
								onChange={(date: Dayjs | null) => handleDateChange('requestedOn', date)}
								renderInput={(props) => <TextField {...props} variant='filled' size='small' />}
							/>
						</Box>
                        <Box sx={{ flex: '0 1 12%' }}>
                            <DatePicker
                                label='Required Before'
                                value={filters.requiredBefore}
                                onChange={(date: Dayjs | null) => handleDateChange('requiredBefore', date)}
                                renderInput={(props) => <TextField {...props} variant='filled' size='small' />}
                            />
                        </Box>
						<Box sx={{ flex: '0 1 12%', display: 'flex' }}>
							<Button
								onClick={handleClearFilters}
								variant='text'
								sx={{
									textTransform: 'none',
									color: 'gray',
									textDecoration: 'underline',
									'&:hover': {
										textDecoration: 'none',
										color: 'darkblue',
									},
								}}
							>
								Clear Filters
							</Button>
						</Box>
					</Box>
				</Collapse>
			</CardContent>
		</Card>
	)
}

export default FilterHeader